import { createSlice, isAnyOf } from "@reduxjs/toolkit";

import type { RootState } from "store/store";

import { api } from "api/generated";

/**
 * Otevřený trénink na serveru už neodpovídá tomu, co má tahle záložka — jiná záložka nebo
 * zařízení ho mezitím spustilo, nebo zavřelo a nahradilo novým. Znovu odeslat to nepomůže,
 * záložka si musí trénink načíst znovu. Druh konfliktu rozhoduje, jestli uložená session
 * v localStorage ještě platí (viz SessionConflictModal). [TD-461]
 */
export type SessionConflict = "alreadyStarted" | "notStarted" | null;

// Texty výjimek z todas-app (`web/graphql/exceptions/session_*.py`).
const CONFLICT_BY_MESSAGE: Record<string, Exclude<SessionConflict, null>> = {
  "Session already started": "alreadyStarted",
  "Session created but not started": "notStarted",
};

// AIDEV-NOTE: Porovnává se `errors[].message` z odpovědi, ne `message` chyby — ten
// graphql-request skládá i z celého requestu, tedy z proměnných s texty odpovědí studenta.
const getConflict = (meta: unknown): SessionConflict => {
  const errors = (meta as { baseQueryMeta?: { response?: { errors?: Array<{ message?: unknown }> } } } | undefined)
    ?.baseQueryMeta?.response?.errors;

  for (const error of errors ?? []) {
    if (typeof error.message === "string" && error.message in CONFLICT_BY_MESSAGE) {
      return CONFLICT_BY_MESSAGE[error.message];
    }
  }

  return null;
};

const initialState = null as SessionConflict;

const sessionConflictSlice = createSlice({
  name: "sessionConflict",
  initialState,
  reducers: {},
  extraReducers: builder => {
    // AIDEV-NOTE: Jen mutace tréninku, které student na `/cviceni` volá. Selhaný přenos
    // (Failed to fetch) konflikt není — ten řeší ErrorModal opakováním a backend je od TD-461
    // na opakovaný start i odpověď idempotentní.
    builder.addMatcher(
      isAnyOf(
        api.endpoints.sessionStart.matchRejected,
        api.endpoints.sessionCreate.matchRejected,
        api.endpoints.checkAnswers.matchRejected,
      ),
      (state, action) => state ?? getConflict(action.meta),
    );
  },
});

export const selectSessionConflict = (state: RootState) => state.sessionConflict;

export default sessionConflictSlice.reducer;
